/** Agent presence (§4.16): derived, never stored. Away (not lost) when the mind's runner is offline. */
import { isRunEnded } from "./state"
import type { Agent, AgentPresence, EntityRef, ProjectState, Run } from "./types"
import { ownerLeft } from "./workers"

export interface PresenceInputs {
  readonly state: ProjectState
  readonly agent: Agent
  readonly queuedWakes: number
  readonly focus?: ReadonlyArray<EntityRef>
}

/** Wave B2 (decision 0057): plain words, never a raw tag or id ("waiting for runner: <tag>" is gone). */
const describeUnmet = (run: Run): string | null => {
  if (run.state._tag !== "waiting") return null
  const u = run.state.unmet[0]
  if (!u) return "waiting for a runner"
  switch (u._tag) {
    case "runner_offline":
      return "its runner is offline"
    case "no_runner_online":
      return "no runner is online"
    default:
      return "waiting for a runner"
  }
}

/**
 * Re-walk of 2026-10-28 (decision 0166 §2): what says a waiting turn needs a person, not time: a login or subscription
 * the asker may not use, a CLI no runner it may use has, or the asker's own choice of subscriptions. Such a turn is
 * "can't work" everywhere (chat header, Home, Team), not "queued".
 */
const ACCESS_WAIT: ReadonlySet<string> = new Set([
  "owner_lock",
  "not_chosen",
  "missing_agent",
  "profile_not_ready",
  "profile_out_of_scope",
])

/** The presence reason of a turn that waits on access (`ACCESS_WAIT`); setup reads it as no problem of its own. */
export const WAITS_FOR_ACCESS = "its turn waits for a subscription it may use"

/**
 * The agent's OWN state (K16): paused → needs attention (a login error, or its owner left, K10) → its live turn
 * (thinking, or queued / away while it waits) → queued wakes → idle. Never "working": live workers and validators it
 * supervises are counted in `activeWorkers` ("supervising n"), not shown as the agent's activity.
 */
export const agentPresence = ({ state, agent, queuedWakes, focus = [] }: PresenceInputs): AgentPresence => {
  const turns = state.runs
    .filter((r) => r.kind === "turn" && r.agentId === agent.id)
    .sort((a, b) => b.requestedAt - a.requestedAt)
  const live = turns.find((r) => !isRunEnded(r)) ?? null
  const last = turns[0] ?? null
  const supervised = state.runs.filter(
    (r) => (r.kind === "worker" || r.kind === "validator") && r.agentId === agent.id && !isRunEnded(r),
  ).length
  const lastActiveAt = last?.endedAt ?? last?.startedAt ?? null
  const base = {
    agentId: agent.id,
    currentRunId: live?.id ?? null,
    focus,
    activeWorkers: supervised,
    queuedWakes,
    lastActiveAt,
  }
  if (agent.status !== "active")
    return { ...base, state: "paused", reason: agent.status === "retired" ? "retired" : null }
  if (ownerLeft(state, agent))
    return {
      ...base,
      state: "needs_attention",
      reason: "its owner left the workspace; take it over in project settings",
    }
  if (!live && last?.stopReason === "auth_error")
    return {
      ...base,
      state: "needs_attention",
      // Plain words for people (decision 0056 glossary): a missing workspace key is named as such.
      // A turn that ran on a runner failed its sign-in there: the coding tool is signed out, not a model key.
      reason: (last.executor ? last.executor._tag === "runner" : agent.mind._tag === "runner")
        ? "its coding tool is signed out on its runner"
        : last.error?.code === "no_api_key"
          ? "no model key: add one in Settings › Model providers"
          : "the model key was refused: check Settings › Model providers",
    }
  // decision 0135: the provider account behind the key is out of credit (a person tops it up; no retry helps)
  if (!live && last?.stopReason === "budget_exhausted" && last.error?.code === "credit_exhausted")
    return {
      ...base,
      state: "needs_attention",
      reason: "the model account is out of credit: check its billing",
    }
  if (live) {
    if (live.state._tag === "waiting") {
      const offline = live.state.unmet.some(
        (u) => u._tag === "runner_offline" || u._tag === "no_runner_online",
      )
      if (offline) return { ...base, state: "away", reason: describeUnmet(live) }
      const unmet = live.state.unmet
      if (unmet.length > 0 && unmet.every((u) => ACCESS_WAIT.has(u._tag)))
        return { ...base, state: "needs_attention", reason: WAITS_FOR_ACCESS }
      return { ...base, state: "queued", reason: describeUnmet(live) }
    }
    if (live.state._tag === "requested") return { ...base, state: "queued", reason: null }
    return { ...base, state: "thinking", reason: null }
  }
  if (queuedWakes > 0) return { ...base, state: "queued", reason: null }
  return { ...base, state: "idle", reason: null }
}
